<template>
  <div
    class="rs-seo-card"
    :class="plain ? '' : 'card'"
  >
    <header
      v-if="plain == false"
      class="rs-seo-card__header"
    >
      <h4>{{ $ts('seoCard.title') }}</h4>
    </header>
    <RsBanner
      v-show="alertType != ''"
      type="help-text-primary"
      size="small"
      :message="$ts('seoCard.defaultTitleUrlMessage', { alertType })"
    />
    <div class="row row-collapse">
      <div
        :class="{
          'col-md-6': hasMetaImage,
          'col-12': !hasMetaImage,
        }"
      >
        <label>
          {{ $ts('seoCard.seoTitle') }}
          <RsListingScoreHeart
            id="seoTitle"
            :show-heart="pName === '' && inListingScore"
          />
        </label>
        <RsFormInput
          id="seoTitle"
          v-model="pName"
          type="text"
          :debounce="100"
          class="flex-fill mb-0"
          @input="$emit('metaTitle', pName)"
        />
        <div class="seoTitle-text">
          {{ seoTitle }}
        </div>
        <div class="strength-bar-wrapper">
          <div class="strength-bar">
            <div
              class="strength-bar__overlay"
              :class="metaTitleStrength.strengthColor"
              :style="{ width: metaTitleStrength.strength + '%' }"
            />
          </div>
          <div class="strength-bar-counter">
            <p>{{ (seoTitle || "").length }}/60</p>
          </div>
        </div>
        <div class="strength-bar-info">
          <div id="tooltip-seo-title">
            <RsIcon
              icon="info-circle"
              weight="fas"
            />
          </div>
          <p>{{ metaTitleStrength.strengthText }}</p>
          <RsTooltip
            target="tooltip-seo-title"
            title="Bottom"
            triggers="click"
            variant="secondary"
            position="bottomright"
          >
            {{ $ts('seoCard.metaTitleTooltip') }}
          </RsTooltip>
        </div>
      </div>
      <div
        v-if="hasMetaImage"
        class="col-md-6"
      >
        <RsFormFile
          id="seo_card_image_upload"
          ref="seoImageUploader"
          :label="$ts('seoCard.metaImage')"
          @on-upload-triggered="$emit('on-upload-triggered', $event)"
        />
        <div
          v-show="metaImageUrl"
          class="mt-1 text-right"
        >
          <label class="mr-2">{{ $ts('seoCard.current') }}</label>
          <a
            :href="metaImageUrl"
            target="_blank"
          >
            {{ $utils.rsFancyFilename(metaImageUrl ?? '') }}
          </a>

          <i class="far fa-times-circle rs-text-error cursor-pointer" @click="$emit('on-image-remove', metaImageUrl)" />
        </div>
      </div>
      <div class="col-md-12">
        <RsFormInput
          v-show="hasUrl"
          v-model="sUrl"
          v-permalink
          :disabled="$rsGlobals.$isTranslateMode"
          :label="$ts('seoCard.seoUrl')"
          type="text"
          :debounce="400"
          class="flex-fill mb-0 text-break"
          @blur="removeSpecialChar()"
          @keydown.native="nameKeydown($event)"
          @input="$emit('metaURL', sUrl)"
        />
        <label>
          {{ $ts('seoCard.metaDescription') }}
          <RsListingScoreHeart
            id="metaDescription"
            :show-heart="mDesc === '' && inListingScore"
          />
        </label>
        <RsFormInput
          id="metaDescription"
          v-model="mDesc"
          class="mb-0 text-break"
          type="text"
          :listing-scores-heart="inListingScore && $rsGlobals.$currentUser.getClientSettingValue('listing_scores_beta') === '1'"
          :allow-resize="false"
          :debounce="100"
          @input="$emit('metaDesc', mDesc)"
        />
      </div>
      <div class="col-md-12">
        <div class="strength-bar-wrapper">
          <div class="strength-bar">
            <div
              class="strength-bar__overlay"
              :class="metaDescStrength.strengthColor"
              :style="{ width: metaDescStrength.strength + '%' }"
            />
          </div>
          <div class="strength-bar-counter">
            <p>{{ (mDesc || "").length }}/155</p>
          </div>
        </div>
        <div class="strength-bar-info">
          <div id="tooltip-seo-desc">
            <RsIcon
              icon="info-circle"
              weight="fas"
            />
          </div>
          <p>{{ metaDescStrength.strengthText }}</p>
          <RsTooltip
            target="tooltip-seo-desc"
            title="Bottom"
            triggers="click"
            variant="secondary"
            position="bottomright"
          >
            {{ $ts('seoCard.metaDescriptionTooltip') }}
          </RsTooltip>
        </div>
      </div>
    </div>
    <div class="row">
      <div class="col-md-12">
        <div class="rt-card">
          <div class="d-flex">
            <div class="text-break">
              <cite>www.websiteurl.com > {{ sUrl }}</cite>
              <h3>{{ pageName }} {{ siteName ? `| ${siteName}` : '' }}</h3>
              <p>{{ getDate() }} - {{ metaDesc }}</p>
            </div>
            <div
              v-show="hasImage"
              class="ml-auto"
            >
              <RsImage
                width="100px"
                height="100px"
                rounded
                :src="metaImageUrl || '/img/default_300x300.png'"
                alt="This is an image"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" scoped>
import { ILiftSettings } from '@llws/lift-entity-interfaces';
import { defineComponent } from 'vue';
import { assertRef } from '@/shared/helpers/assert-ref';
import RsFormFile from '@/components/_global/RsFormFile.vue';

export default defineComponent({
  name: 'RsSeoCard',

  props: {
    metaDesc: {
      type: String,
      default: '',
    },
    plain: {
      type: Boolean,
      default: false,
    },
    hasImage: {
      type: Boolean,
      default: true,
    },
    inListingScore: {
      type: Boolean,
      default: false,
    },
    metaImageUrl: String,
    pageName: String,
    alertType: {
      type: String,
      default: '',
    },
    url: String,
    hasUrl: {
      type: Boolean,
      default: false,
    },
    hasMetaImage: {
      type: Boolean,
      default: true,
    },
    displayUrl: {
      type: String,
      default: '',
    },
    altSiteName: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      moduleKeys: ['global'],
      show: true,
      metaDescStrength: {} as Record<string, any>,
      metaTitleStrength: {} as Record<string, any>,
      mDesc: this.metaDesc,
      pName: this.pageName,
      sUrl: this.url,
      seoTitle: '',
    };
  },

  computed: {
    isWebsitePage(): boolean {
      return Boolean(this.$rsGlobals.$sidebarHeaderData?.data?.siteKey);
    },

    siteName(): string {
      const websiteSiteName = this.isWebsitePage
        ? this.getLiftSettingValue(this.$rsGlobals.$sidebarHeaderData?.data?.liftSettings)
        : '';
      const clientSiteName = this.getLiftSettingValue(this.$rsGlobals.$currentClient?.liftSettings);

      return this.altSiteName
        || websiteSiteName
        || clientSiteName
        || this.$rsGlobals.$currentClient.name
        || '';
    },
  },

  watch: {
    siteName() {
      this.seoTitle = `${this.pageName} ${this.siteName ? `| ${this.siteName}` : ''}`;
    },
    pageName() {
      this.pName = `${this.pageName}`;
      this.seoTitle = `${this.pName}${this.siteName ? ` | ${this.siteName}` : ''}`;
      this.metaTitleStrength = this.getMetaStrength(this.seoTitle, 23, 46, 60);
    },
    metaDesc() {
      this.mDesc = `${this.metaDesc}`;
      this.metaDescStrength = this.getMetaStrength(this.mDesc, 54, 107, 155);
    },
    url() {
      this.sUrl = `${this.url}`;
    },
    pName() {
      this.seoTitle = `${this.pName}${this.siteName ? ` | ${this.siteName}` : ''}`;
      this.metaTitleStrength = this.getMetaStrength(this.seoTitle, 23, 46, 60);
    },
    seoTitle() {
      this.metaTitleStrength = this.getMetaStrength(this.seoTitle, 23, 46, 60);
    },
    mDesc() {
      this.metaDescStrength = this.getMetaStrength(this.mDesc, 54, 107, 155);
    },
    '$rsGlobals.$language': function () {
      // Recalculate strength text when language changes to ensure translations are applied
      this.metaTitleStrength = this.getMetaStrength(this.seoTitle, 23, 46, 60);
      this.metaDescStrength = this.getMetaStrength(this.mDesc, 54, 107, 155);
    },
  },

  mounted() {
    this.seoTitle = `${this.pName}${this.siteName ? ` | ${this.siteName}` : ''}`;
    this.metaTitleStrength = this.getMetaStrength(this.seoTitle, 23, 46, 60);
    this.metaDescStrength = this.getMetaStrength(this.mDesc, 54, 107, 155);
  },
  methods: {
    getLiftSettingValue(settings: ILiftSettings[] | null | undefined, key: string = 'site_name'): string {
      return settings?.find((setting: ILiftSettings) => setting.key === key)?.value || '';
    },

    nameKeydown(e: KeyboardEvent) {
      if (/[^a-zA-Z0-9 -]/.test(e.key)) {
        e.preventDefault();
      }
    },

    removeSpecialChar() {
      this.sUrl = this.sUrl?.replace(/[^a-zA-Z0-9 -]/g, '-');
    },

    // Sets the date for the SEO preview
    getDate() {
      const months = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec',
      ];
      const currentDate = new Date();
      return (
        `${months[currentDate.getMonth()]
        } ${
          currentDate.getDate()
        }, ${
          currentDate.getFullYear()}`
      );
    },

    getMetaStrength(
      input: string = '',
      avgLength: number,
      optLength: number,
      maxLength: number,
    ) {
      const { length } = input;
      const returnVal: any = {
        strength: (length / maxLength) * 100,
        strengthColor: 'error',
        strengthText: this.$ts('seoCard.strengthTooShort'),
      };

      if (length >= avgLength) {
        returnVal.strengthColor = 'warning';
        returnVal.strengthText = this.$ts('seoCard.strengthAverage');
      }

      if (length >= optLength) {
        returnVal.strengthColor = 'success';
        returnVal.strengthText = this.$ts('seoCard.strengthOptimal');
      }

      if (length > maxLength) {
        returnVal.strengthColor = 'error';
        returnVal.strengthText = this.$ts('seoCard.strengthTooLong');
      }

      return returnVal;
    },

    /**
     * Calls the `resetFile` method on the RsFormFile instance, handy for when a parent component
     * facilitates an upload and needs to reset the form file's state.
     */
    resetFile() {
      assertRef(this.$refs.seoImageUploader, RsFormFile)?.resetFile();
    },
  },

});
</script>

<style lang="scss">
@import "@/styles/_variables.scss";
.rs-seo-card {
  &__header {
    margin-bottom: 1rem;
    h4 {
      margin: 0;
      font-weight: 700;
      font-size: 18px;
      color: $secondary-color-dark;
    }
  }
  .details {
    margin-top: 10px;
  }
  .rt-card {
    margin-top: 1rem;
    padding: 1.5rem;
    border: 1px solid $tertiary-gray;
    border-radius: $global-radius;
    font-family: "Arial";
    h3 {
      padding-top: 4px;
      font-family: "Arial";
      font-size: rem-calc(20);
      color: #1a0dab;
    }
    cite {
      font-size: 14px;
      color: #202124;
      font-weight: normal;
      font-style: normal;
      overflow-wrap: break-word;
      word-break: break-word;
    }
    p {
      font-weight: normal;
      color: #4d5156;
      line-height: 1.58;
      word-wrap: break-word;
    }
  }
  .seoTitle-text {
    font-size: 0.825rem;
    padding-left: 5px;
  }
  p {
    display: inline-block;
    margin-bottom: 0.5rem;
    font-size: 0.875rem;
    font-weight: 600;
  }
  .strength-bar-wrapper {
    margin-top: 0.25rem;
    display: flex;
    align-items: center;
    p {
      margin: 0;
    }

    .strength-bar,
    .strength-bar__overlay {
      width: 100%;
      height: 8px;
      border-radius: 15px;
    }
    .strength-bar {
      position: relative;
      overflow: hidden;
      border-radius: 15px;
      background-color: #eee;
      &:before,
      &:after {
        content: "";
        position: absolute;
        top: 0;
        bottom: 0;
        width: 2px;
        background-color: $white;
        opacity: 0.4;
      }
      &:before {
        left: 33%;
      }
      &:after {
        left: 66%;
      }
      &__overlay {
        transition: width 500ms;
        &.success {
          background-color: $primary-color;
        }
        &.error {
          background-color: $error;
        }
        &.warning {
          background-color: $warning;
        }
      }
    }
    .strength-bar-counter {
      margin-left: 1rem;
      min-width: 55px;
      font-size: 0.75rem;
      text-align: right;
    }
  }
  #tooltip-seo-title,
  #tooltip-seo-desc {
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
  }
  .strength-bar-info {
    display: flex;
    align-items: center;
    margin-bottom: 1rem;
    .rs-icon {
      margin-right: 0.25rem;
      color: $tertiary-gray;
    }
    p {
      margin: 0;
      color: $tertiary-muted;
      font-size: 0.875rem;
    }
  }
  #metaDescription {
    overflow-y: auto !important;
  }
}
</style>
